import { useCallback, useMemo, useState } from "react"

import { GoogleMap, StreetViewPanorama, StreetViewService, useLoadScript } from "@react-google-maps/api"

import { ZoomButtons } from "@l2r-front/l2r-map"
import { useIsMobileDevice } from "@l2r-front/l2r-ui"

import { useImageCollectionDispatchContext } from "../../contexts/ImageCollectionContext"

import { StreetViewError } from "./StreetView.error"
import * as Styled from "./StreetView.styled"


export const StreetView = (props) => {

    const { initialPosition } = props
    const [streetViewState, setStreetViewState] = useState(null)
    const [streetViewError, setStreetViewError] = useState(false)
    const [streetViewRef, setStreetViewRef] = useState(null)
    const { setDisplayedImagePosition } = useImageCollectionDispatchContext()
    const isMobile = useIsMobileDevice()

    const position = useMemo(() => {
        return {
            lat: initialPosition?.[1],
            lng: initialPosition?.[0],
        }
    }, [initialPosition])

    const onLoad = useCallback((streetViewService) => {
        setStreetViewState(null)
        return streetViewService.getPanorama({
            location: position,
            radius: 50,
        }, (data, status) => {
            if (status === "OK" && !!data) {
                setStreetViewState(data)
            } else {
                setStreetViewError(true)
            }
        })
    }, [position])

    const mapStyle = useMemo(() => {
        const displayed = streetViewState && !streetViewError

        return ({
            ...Styled.mapContainerStyle,
            display: displayed ? "block" : "none",
        })
    }, [streetViewError, streetViewState])

    const onPositionChanged = useCallback(() => {
        if (streetViewRef) {
            setDisplayedImagePosition({ lat: streetViewRef.getPosition().lat(), lng: streetViewRef.getPosition().lng() })
        }
    }, [streetViewRef, setDisplayedImagePosition])

    const message = useMemo(() => {
        return streetViewError ? <StreetViewError />
            : !streetViewState ? <Styled.Skeleton />
                : null
    }, [streetViewError, streetViewState])

    const { isLoaded, loadError } = useLoadScript({
        googleMapsApiKey: process.env["NX_PUBLIC_GOOGLEMAPTOKEN"],
    })

    const handleZoomIn = useCallback(() => {
        if (streetViewRef) {
            const currentZoom = streetViewRef.getZoom()
            streetViewRef.setZoom(currentZoom + 1)
        }
    }, [streetViewRef])

    const handleZoomOut = useCallback(() => {
        if (streetViewRef) {
            const currentZoom = streetViewRef.getZoom()
            streetViewRef.setZoom(currentZoom - 1)
        }
    }, [streetViewRef])

    if (loadError || !position.lat || !position.lng) {
        return <StreetViewError />
    }

    if (!isLoaded) {
        return <Styled.Skeleton />
    }

    return <Styled.MapWrapper>
        <GoogleMap
            center={position}
            id="google-map"
            mapContainerStyle={mapStyle}
            zoom={17}
        >
            {
                streetViewState?.tiles && <StreetViewPanorama
                    options={{
                        disableDefaultUI: true,
                        addressControl: true,
                        imageDateControl: true,
                        motionTracking: false,
                        motionTrackingControl: false,
                        zoomControl: false,
                    }}
                    position={position}
                    streetViewPanorama={streetViewState}
                    visible
                    onLoad={e => {
                        setStreetViewRef(e)
                        if (!streetViewState?.listener) {
                            setStreetViewState(previousState => ({
                                ...previousState,
                                listener: true,
                            }))
                        }
                    }}
                    onPositionChanged={onPositionChanged}
                />
            }
            <StreetViewService
                onLoad={onLoad}
            />
            {!isMobile && <Styled.BottomRightAnchor>
                <ZoomButtons
                    onZoomIn={handleZoomIn}
                    onZoomOut={handleZoomOut}
                />
            </Styled.BottomRightAnchor>}
        </GoogleMap>
        {message}
    </Styled.MapWrapper>
}